import { ComponentProps, useCallback, useMemo } from 'react';
import { BordSelect, BordSwitch } from 'bord-design-system';
import InspectionFieldRow from './InspectionFieldRow/InspectionFieldRow';
import {
  CHARGER_CONDITION_SELECT_OPTIONS,
  CHARGER_CONDITIONS,
  FUNCTIONAL_OS_SELECT_OPTIONS,
  TOOL_INSPECTION_FIELD_ID as IDS,
  IInitialFieldConfig,
  LANGUAGE_SELECT_OPTIONS,
  TToolInspectionParsedValue
} from '../../../../../../../../../../constants/toolInspection.constants';
import { formatSingleSelectValueAsLabel, getFieldLabel, getFieldValue } from '../../../../utils/toolInspection.utils';

import './InspectionFieldsContainer.scss';

type TBordSelectOptions = NonNullable<ComponentProps<typeof BordSelect>['options']>;

interface ILaptopInspectionFieldsContainerProps {
  fields: Record<number, IInitialFieldConfig>;
  setFieldIndividual: (id: number, value: TToolInspectionParsedValue) => void;
  isDisabled: boolean;
}

const LaptopInspectionFieldsContainer = ({
  fields,
  setFieldIndividual,
  isDisabled: formDisabled
}: ILaptopInspectionFieldsContainerProps) => {
  const hasCable = !!getFieldValue<boolean>(fields, IDS.INCLUDE_CABLE);
  const hasAdapter = !!getFieldValue<boolean>(fields, IDS.INCLUDE_CHARGER);
  const hasBox = !!getFieldValue<boolean>(fields, IDS.INCLUDE_BOX);

  const fieldDisables = useMemo(
    () => ({
      hasOriginalCable: !hasCable,
      hasOriginalBox: !hasBox,
      chargerCondition: !hasCable && !hasAdapter
    }),
    [hasCable, hasAdapter, hasBox]
  );

  const syncLaptopChargerCondition = useCallback(
    (cableActive: boolean, adapterActive: boolean) => {
      const currentCondition = getFieldValue<string>(fields, IDS.CHARGER_CONDITION);

      if (!cableActive && !adapterActive) {
        setFieldIndividual(IDS.CHARGER_CONDITION, CHARGER_CONDITIONS.NONE);
      } else if (currentCondition === CHARGER_CONDITIONS.NONE) {
        setFieldIndividual(IDS.CHARGER_CONDITION, CHARGER_CONDITIONS.LIKE_NEW);
      }
    },
    [fields, setFieldIndividual]
  );

  const handleIncludesCableChange = (next: boolean) => {
    setFieldIndividual(IDS.INCLUDE_CABLE, next);
    if (!next) setFieldIndividual(IDS.HAS_ORIGINAL_CABLE, false);
    syncLaptopChargerCondition(next, hasAdapter);
  };

  const handleIncludesAdapterChange = (next: boolean) => {
    setFieldIndividual(IDS.INCLUDE_CHARGER, next);
    syncLaptopChargerCondition(hasCable, next);
  };

  const handleIncludesBoxChange = (next: boolean) => {
    setFieldIndividual(IDS.INCLUDE_BOX, next);
    if (!next) setFieldIndividual(IDS.HAS_ORIGINAL_BOX, false);
  };

  const renderSwitch = (id: number, onChange?: (v: boolean) => void, extraDisabled = false) => (
    <li className="inspectionFieldsListItem">
      <InspectionFieldRow label={getFieldLabel(fields, id)} isDisabled={formDisabled || extraDisabled}>
        <BordSwitch
          checked={!!getFieldValue<boolean>(fields, id)}
          onChange={onChange || (val => setFieldIndividual(id, val))}
          disabled={formDisabled || extraDisabled}
        />
      </InspectionFieldRow>
    </li>
  );

  const renderSelect = (
    id: number,
    options: ReadonlyArray<{ value: string | number; label: string }>,
    extraDisabled = false
  ) => (
    <li className="inspectionFieldsListItem">
      <InspectionFieldRow label={getFieldLabel(fields, id)} isDisabled={formDisabled || extraDisabled}>
        {formDisabled ? (
          <span className="inspectionFieldRowValue inspectionFieldRowValueTruncate">
            {formatSingleSelectValueAsLabel(getFieldValue<string | number>(fields, id), options)}
          </span>
        ) : (
          <BordSelect
            triggerVariant="bare"
            dropdownSide="bottom"
            indicatorPlacement="start"
            dropdownMaxHeight="14rem"
            value={getFieldValue<string>(fields, id) || ''}
            options={options as TBordSelectOptions}
            disabled={formDisabled || extraDisabled}
            onChange={val => setFieldIndividual(id, val)}
          />
        )}
      </InspectionFieldRow>
    </li>
  );

  return (
    <div className="inspectionFieldsContainer">
      <ul className="inspectionFieldsList">
        {renderSwitch(IDS.HAS_STICKERS)}

        {renderSwitch(IDS.IS_CLEAN)}

        {renderSwitch(IDS.INCLUDE_CABLE, handleIncludesCableChange)}

        {renderSwitch(IDS.HAS_ORIGINAL_CABLE, undefined, fieldDisables.hasOriginalCable)}

        {renderSwitch(IDS.INCLUDE_CHARGER, handleIncludesAdapterChange)}

        {renderSwitch(IDS.INCLUDE_BOX, handleIncludesBoxChange)}

        {renderSwitch(IDS.HAS_ORIGINAL_BOX, undefined, fieldDisables.hasOriginalBox)}

        {renderSwitch(IDS.TURNS_ON)}

        {renderSwitch(IDS.SCREEN_FUNCTIONAL)}

        {renderSwitch(IDS.KEYBOARD_FUNCTIONAL)}

        {renderSelect(IDS.FUNCTIONAL_OS, FUNCTIONAL_OS_SELECT_OPTIONS)}

        {renderSelect(IDS.LANGUAGE, LANGUAGE_SELECT_OPTIONS)}

        {renderSelect(IDS.CHARGER_CONDITION, CHARGER_CONDITION_SELECT_OPTIONS, fieldDisables.chargerCondition)}
      </ul>
    </div>
  );
};

export default LaptopInspectionFieldsContainer;
